import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import NavLink from "components/menus/NavLink";
import { svgProps } from "components/icons/SvgContainer";

const NavDropdown = ({
  iconProps = { height: 12, width: 12 },
  toggleClassName = "",
  children,
  toggle,
  right,
  active,
  icon,
  background,
  tabIndex,
  noTopMargin,
}) => {
  const [show, setShow] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const getMenuItems = () => {
    if (!menuRef.current) return [];
    // Get all focusable elements in the dropdown menu
    const items = Array.from(menuRef.current.querySelectorAll('a, button, [tabindex="0"]'));
    // Filter out disabled items
    return items.filter((item) => {
      const isDisabled = item.hasAttribute('disabled')
                        || item.getAttribute('aria-disabled') === 'true'
                        || item.classList.contains('disabled');
      return !isDisabled;
    });
  };

  const toggleShow = (ev) => {
    if (ev) ev.preventDefault();
    setShow((prevShow) => {
      const newShow = !prevShow;
      if (newShow) {
        // Focus first menu item after dropdown opens
        setTimeout(() => {
          const items = getMenuItems();
          if (items[0]) items[0].focus();
        }, 0);
      }
      return newShow;
    });
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShow(false);
    }
  };

  const handleKeyDown = (event) => {
    if (!show) return;

    const menuItems = getMenuItems();
    const currentIndex = menuItems.findIndex(item => item === document.activeElement);

    switch (event.key) {
      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          // Shift+Tab: move backwards
          if (currentIndex === -1 || currentIndex === 0) {
            if (menuItems[menuItems.length - 1]) {
              menuItems[menuItems.length - 1].focus();
            }
          } else if (menuItems[currentIndex - 1]) {
            menuItems[currentIndex - 1].focus();
          }
        } else if (currentIndex === -1 || currentIndex >= menuItems.length - 1) {
          // Tab: move forwards
          if (menuItems[0]) menuItems[0].focus();
        } else if (menuItems[currentIndex + 1]) {
          menuItems[currentIndex + 1].focus();
        }
        break;
      case 'Enter':
      case ' ':
        if (currentIndex !== -1) {
          event.preventDefault();
          if (menuItems[currentIndex]) menuItems[currentIndex].click();
          setShow(false);
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (currentIndex === -1 || currentIndex >= menuItems.length - 1) {
          if (menuItems[0]) menuItems[0].focus();
        } else if (menuItems[currentIndex + 1]) {
          menuItems[currentIndex + 1].focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (currentIndex === -1 || currentIndex === 0) {
          if (menuItems[menuItems.length - 1]) {
            menuItems[menuItems.length - 1].focus();
          }
        } else if (menuItems[currentIndex - 1]) {
          menuItems[currentIndex - 1].focus();
        }
        break;
      case 'Escape':
        event.preventDefault();
        setShow(false);
        if (dropdownRef.current) {
          const navLink = dropdownRef.current.querySelector('.nav-link');
          if (navLink) navLink.focus();
        }
        break;
      case 'Home':
        event.preventDefault();
        if (menuItems[0]) menuItems[0].focus();
        break;
      case 'End':
        event.preventDefault();
        if (menuItems[menuItems.length - 1]) {
          menuItems[menuItems.length - 1].focus();
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (show) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    }

    // Cleanup function to remove the event listeners
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show]);

  // Children are often wrapped in fragments by conditional rendering, so recurse rather than
  // inspecting only the top level, or role="menu" ends up with no menuitem descendants.
  const withMenuRoles = nodes => React.Children.map(nodes, (child) => {
    if (!React.isValidElement(child)) return child;

    if (child.type === React.Fragment) {
      return <React.Fragment>{withMenuRoles(child.props.children)}</React.Fragment>;
    }
    if (child.type === NavLink) {
      return React.cloneElement(child, { role: 'menuitem' });
    }
    if (typeof child.props.className === 'string' && child.props.className.includes('dropdown-divider')) {
      return React.cloneElement(child, { role: 'separator' });
    }
    return child;
  });

  const childrenWithMenuItemRole = withMenuRoles(children);

  return (
    <div
      ref={dropdownRef}
      className={`NavDropdown nav-item dropdown ${background ?? ""}`}
    >
      <NavLink
        className={`${toggleClassName} ${active ? "active" : ""}`}
        onClick={toggleShow}
        icon={icon}
        iconProps={iconProps}
        ariaControls={`id-${toggle}`}
        ariaExpanded={show}
        tabIndex={tabIndex}
        ariaHaspopup="true"
      >
        {toggle}
      </NavLink>
      <div
        ref={menuRef}
        id={`id-${toggle}`}
        className={`dropdown-menu ${noTopMargin ? "mt-0" : ""} ${
          background ?? ""
        } ${show ? "show" : "hide"} ${right ? "dropdown-menu-right" : ""}`}
        role="menu"
        aria-orientation="vertical"
      >
        {childrenWithMenuItemRole}
      </div>
    </div>
  );
};

NavDropdown.propTypes = {
  iconProps: PropTypes.shape({ ...svgProps }),
  toggleClassName: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.element),
  ]).isRequired,
  toggle: PropTypes.string.isRequired,
  right: PropTypes.bool,
  active: PropTypes.bool,
  icon: PropTypes.string,
  background: PropTypes.string,
  tabIndex: PropTypes.string,
  noTopMargin: PropTypes.bool,
};

export default NavDropdown;
